import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { getFeaturedStation, getStation } from "@/lib/service";
import { StationResultProps } from "@/lib/type";
import { Button } from "./button";
import { Phone } from "lucide-react";

interface StationResultPropsCategory {
  categoryName: string;
  locationList: StationResultProps[];
}

export interface AddressInputProps
  extends React.HTMLAttributes<HTMLDivElement> {
  value?: StationResultProps;
  asChild?: boolean;
  id: string;
  placeholder: string;
  prefix?: string;
  popUpClassName?: string;
  onSelect: any;
  showError?: boolean;
}

const AddressInput = React.forwardRef<HTMLDivElement, AddressInputProps>(
  (
    {
      value,
      className,
      asChild = false,
      id,
      placeholder,
      prefix,
      popUpClassName,
      onSelect,
      children,
      showError = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "div";

    const [isOpen, setIsOpen] = React.useState(false);
  const [text, setText] = React.useState(value?.name ?? "");
    const [data, setData] = React.useState<StationResultPropsCategory[]>([]);
    const [addressShow, setAddressShow] = React.useState(false);
    const [address, setAddress] = React.useState<
      StationResultProps | undefined
    >(value);

    const handleClickOutside = (event: MouseEvent) => {
      const div = document.querySelector(`#placeCard-${id}`);
      if (div && !div.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    React.useEffect(() => {
      getFeaturedStationList();
      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    React.useEffect(() => {
      if(value != undefined){
        setAddress(value);
        setText(value.name);
      }
    },[value]);

    const getFeaturedStationList = async () => {
      const currentStationList: StationResultPropsCategory[] = [];
      const stationList = await getFeaturedStation();

      stationList.forEach((element: StationResultProps) => {
        const category = (element as any)._region
          ? ((element as any)._region === "MY" ? "Malaysia" : "Thailand")
          : (element.type_description || "Other");
        const index = currentStationList.findIndex(
          (e) => e.categoryName === category
        );

        if (index == -1) {
          currentStationList.push({
            categoryName: category,
            locationList: [element],
          });
        } else {
          currentStationList[index].locationList.push(element);
        }
      });

      setData(currentStationList);
    }

    const getStationList = async () => {
      const currentStationList: StationResultPropsCategory[] = [];
      const stationList = await getStation(text);

      stationList.forEach((element: StationResultProps) => {
        const category = (element as any)._region
          ? ((element as any)._region === "MY" ? "Malaysia" : "Thailand")
          : (element.type_description || "Other");
        const index = currentStationList.findIndex(
          (e) => e.categoryName === category
        );

        if (index == -1) {
          currentStationList.push({
            categoryName: category,
            locationList: [element],
          });
        } else {
          currentStationList[index].locationList.push(element);
        }
      });

      setData(currentStationList);
    };

    React.useEffect(() => {
      if (text.length >= 3) {
        getStationList();
      }
    }, [text]);
    return (
      <Comp key={id} className={cn("relative", className)} ref={ref} {...props}>
        <div
          className={`border-[1px] rounded-md p-2 flex ${showError ? "border-red-500" : "border-gray-300"}`}
        >
          {prefix == null ? <></> : <img src={prefix} />}
          <input
            id={id}
            className={`ml-2 w-full outline-0 ${text == "" ? "" : "font-bold"}`}
            placeholder={placeholder}
            value={text}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            name={Math.random().toString(36).substring(2)}
            onChange={(event) => {
              setText(event.target.value);
              if (event.target.value.length >= 3) {
                setIsOpen(true);
              } else if (event.target.value.length === 0) {
                setIsOpen(true);
                getFeaturedStationList();
              }
            }}
            onClick={() => setIsOpen(true)}
          />
          {text == "" ? (
            <></>
          ) : (
            <FontAwesomeIcon
              icon={faCircleXmark}
              className="my-auto mr-2 text-gray-300 cursor-pointer"
              onClick={() => {
                setIsOpen(true);
                setText("");
                getFeaturedStationList();
              }}
            />
          )}
        </div>
        <div className={`text-xs ${showError ? "text-red-500" : "hidden"} font-medium flex flex-row items-center space-x-1 mt-1`}>
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>{placeholder} cannot be empty</span>
        </div>
        {isOpen ? (
          <div
            id={`placeCard-${id}`}
            className={cn(
              "absolute top-12 left-0 z-50 flex flex-row overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl transition-[width] duration-300 ease-out",
              addressShow
                ? "w-full md:w-[860px] max-w-[calc(100vw-3rem)]"
                : "w-full md:w-[340px]",
              popUpClassName
            )}
          >
            <div className={cn("relative overflow-y-auto max-h-[60vh] py-2", addressShow ? "md:w-[300px] md:shrink-0" : "w-full")}>
              {data.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-1 py-10 text-center text-sm text-gray-400">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-base" />
                  <span>No locations found</span>
                </div>
              ) : (
                data.map((e) => (
                  <React.Fragment key={e.categoryName}>
                    <div className="sticky top-0 z-10 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                      {e.categoryName}
                    </div>
                    {e.locationList?.map((child) => {
                      const isSelected = address?.rw_station_id === child.rw_station_id;
                      return (
                        <div
                          key={child.rw_station_id}
                          className={cn(
                            "flex flex-row items-center gap-3 border-b border-gray-100 px-4 py-3 cursor-pointer transition-colors last:border-b-0",
                            isSelected ? "bg-green-50 text-green-700" : "hover:bg-green-50"
                          )}
                          onMouseEnter={() => {
                            setAddress(child);
                            setAddressShow(true);
                          }}
                          onClick={() => {
                            setIsOpen(false);
                            setText(child.name);
                            onSelect(child);
                          }}
                        >
                          <img
                            src={`/icon/${child?.type_description?.toLowerCase()}.svg`}
                            className="h-4 w-4 flex-shrink-0"
                          />
                          <span className="text-sm">{child.name}</span>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))
              )}
            </div>
            {addressShow && (
              <div className="hidden md:flex md:flex-1 md:min-w-0 flex-row border-l border-gray-100 animate-in fade-in slide-in-from-left-4 duration-300">
                <div className="flex-1 min-w-0 overflow-y-auto max-h-[60vh] p-4 text-start space-y-3 text-sm">
                  <div className="space-y-1">
                    <p className="font-bold leading-snug">{address?.name}</p>
                    <div className="flex flex-col gap-0 text-xs uppercase text-gray-400">
                      {address?.address.street && <p>{address.address.street}</p>}
                      {(address?.address.postal_code || address?.address.city) && (
                        <p>{address?.address.postal_code} {address?.address.city}</p>
                      )}
                      {address?.address.country === '1' && <p>MALAYSIA</p>}
                    </div>
                  </div>

                  {(() => {
                    const days: { label: string; time?: StationResultProps["schedule"]["weekday"] }[] = [
                      { label: "MON", time: address?.schedule?.weekday },
                      { label: "TUE", time: address?.schedule?.weekday },
                      { label: "WED", time: address?.schedule?.weekday },
                      { label: "THU", time: address?.schedule?.weekday },
                      { label: "FRI", time: address?.schedule?.weekday },
                      { label: "SAT", time: address?.schedule?.saturday },
                      { label: "SUN", time: address?.schedule?.sunday },
                    ];
                    const hasAnyHours = days.some((d) => d.time?.open && d.time?.close);
                    if (!hasAnyHours) {
                      return null;
                    }
                    return (
                      <div className="grid grid-cols-[2.25rem_1fr] gap-x-3 gap-y-1 text-xs">
                        {days.map((d) => (
                          <React.Fragment key={d.label}>
                            <span className="font-bold text-black">{d.label}</span>
                            <span className="text-gray-500 whitespace-nowrap">
                              {d.time?.open && d.time?.close ? `${d.time.open} - ${d.time.close}` : "Closed"}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                    );
                  })()}

                  <p className="text-xs text-gray-400">
                    These opening hours may vary due to public holidays.
                  </p>

                  {address?.contact?.phone1 && (
                    <div className="space-y-1">
                      <p className="font-bold text-xs">Pick up during closing hours</p>
                      <Button
                        variant="outline"
                        className="flex items-center space-x-2 border-gray-300 text-black rounded-full group"
                      >
                        <Phone className="text-green-600 group-hover:text-white h-4 w-4" />
                        <span className="underline font-medium">{address.contact.phone1}</span>
                      </Button>
                    </div>
                  )}
                </div>
                <div className="w-[300px] shrink-0 relative bg-gray-100">
                  {address?.location?.latitude &&
                    address?.location?.longitude &&
                    address.location.latitude !== "undefined" &&
                    address.location.longitude !== "undefined" && (
                      <>
                        <iframe
                          src={`https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d15000!2d${address.location.longitude}!3d${address.location.latitude}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus!4v1615554548693!5m2!1sen!2sus`}
                          className="w-full h-full outline-0 ring-0 border-0"
                          allowFullScreen
                          loading="eager"
                        ></iframe>
                        <div
                          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none"
                          style={{
                            width: "36px",
                            height: "36px",
                            backgroundImage: `url('/icon/europcar.png')`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "center",
                            filter: "drop-shadow(0 4px 3px rgb(0 0 0 / 0.07)) drop-shadow(0 2px 2px rgb(0 0 0 / 0.06))",
                          }}
                        />
                      </>
                    )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <></>
        )}
      </Comp>
    );
  }
);

AddressInput.displayName = "AddressInput";

export { AddressInput };
